import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import MainLayout from '../components/MainLayout';
import Player from '../components/player';
import epgParser from '../services/epgParser';
import apiService from '../services/api';
import LazyImage from '../components/LazyImage';

const GuideContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #141414;
  color: #fff;
  overflow: hidden;
`;

const TopSection = styled.div`
  display: flex;
  height: 40vh;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const PlayerWrapper = styled.div`
  width: 40%;
  background: #000;
  position: relative;
`;

const ProgramDetails = styled.div`
  width: 60%;
  padding: 30px;
  background: linear-gradient(to right, #1a1033, #2b1b54);
  overflow-y: auto;
`;

const ProgramTitle = styled.h1`
  font-size: 32px;
  margin: 0 0 10px 0;
  color: #fff;
`;

const ProgramTime = styled.div`
  font-size: 18px;
  color: #aaa;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProgramDesc = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  max-width: 800px;
`;

const ChannelBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.1);
  padding: 5px 10px;
  border-radius: 4px;
  margin-bottom: 15px;
  
  img {
    width: 30px;
    height: 30px;
    object-fit: contain;
  }
  
  span {
    font-weight: 600;
  }
`;

const GridSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const TimeHeader = styled.div`
  display: flex;
  height: 50px;
  background: #1a1033;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-left: 200px; // Espaço para a coluna de canais
  overflow: hidden;
  position: relative;
`;

const TimeSlot = styled.div`
  min-width: 200px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
`;

const GridBody = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #141414;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 4px;
  }
`;

const ChannelRow = styled.div`
  display: flex;
  height: 80px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const ChannelHeader = styled.div`
  width: 200px;
  min-width: 200px;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 15px;
  background: #1a1033;
  position: sticky;
  left: 0;
  z-index: 10;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  gap: 10px;
  cursor: pointer;
  
  &:hover {
    background: #2b1b54;
  }
  
  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    padding: 2px;
  }
  
  span {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ProgramsTrack = styled.div`
  display: flex;
  position: relative;
  height: 100%;
`;

const ProgramBlock = styled.div`
  position: absolute;
  height: 70px; // Um pouco menor que a linha para margem
  top: 5px;
  left: ${props => props.$left}px;
  width: ${props => props.$width}px;
  background: ${props => props.$active ? '#E50914' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? '#F40D17' : 'rgba(255, 255, 255, 0.2)'};
    z-index: 5;
    transform: scale(1.02);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  }
  
  h4 {
    margin: 0 0 4px 0;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  span {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const CurrentTimeLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${props => props.$left}px;
  width: 2px;
  background: #E50914;
  z-index: 20;
  pointer-events: none;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -4px;
    width: 10px;
    height: 10px;
    background: #E50914;
    border-radius: 50%;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(20, 20, 20, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  color: white;
  font-size: 18px;
  flex-direction: column;
  gap: 20px;
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 10px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: #E50914;
  width: ${props => props.$percent}%;
  transition: width 0.3s ease;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
`;

const TVGuide = () => {
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ message: 'Iniciando...', percent: 0 });
  const [channels, setChannels] = useState([]);
  const [programs, setPrograms] = useState({});
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Configurações da grade
  const PIXELS_PER_MINUTE = 4; // Largura de cada minuto na grade
  const HOUR_WIDTH = PIXELS_PER_MINUTE * 60;
  const GRID_START_HOUR = new Date().getHours(); // Começar a grade na hora atual

  const gridRef = useRef(null);
  const timeHeaderRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // Configurar callback de progresso
      epgParser.setProgressCallback((progress) => {
        setLoadingProgress(progress);
      });

      try {
        // Carregar canais da API primeiro para ter as URLs de stream
        if (!apiService.isLoaded) {
          setLoadingProgress({ message: 'Carregando lista de canais...', percent: 10 });
          await apiService.loadChannels();
        }

        const epgData = await epgParser.fetchAndParse('http://bstv.me/xmltv.php?username=56568605&password=89231');

        setLoadingProgress({ message: 'Finalizando...', percent: 98 });

        // Mapear canais do EPG com os canais da API (pelo ID ou Nome)
        // Otimização: Criar um Map para busca rápida de canais da API
        const apiChannelsMap = new Map();
        apiService.channels.forEach(c => {
          apiChannelsMap.set(c.id, c);
          apiChannelsMap.set(c.name.toLowerCase(), c);
        });

        const enrichedChannels = [];
        const channelsWithEpg = epgData.channels;
        const processedChannelIds = new Set();

        // Limitar o número de canais processados inicialmente para melhorar performance
        // Carregar apenas canais que temos na playlist
        for (const epgChannel of channelsWithEpg) {
          // Evitar duplicatas
          if (processedChannelIds.has(epgChannel.id)) continue;

          const apiChannel = apiChannelsMap.get(epgChannel.id) || apiChannelsMap.get(epgChannel.name.toLowerCase());

          if (apiChannel) {
            processedChannelIds.add(epgChannel.id);
            enrichedChannels.push({
              ...epgChannel,
              url: apiChannel.url,
              apiId: apiChannel.id
            });
          }
        }

        setChannels(enrichedChannels);
        setPrograms(epgData.programs);

        // Selecionar primeiro canal
        if (enrichedChannels.length > 0) {
          const firstChannel = enrichedChannels[0];
          setSelectedChannel(firstChannel);

          // Encontrar programa atual
          const now = new Date();
          const currentProg = epgData.programs[firstChannel.id]?.find(p =>
            p.start <= now && p.stop > now
          );
          setSelectedProgram(currentProg || null);
        }

      } catch (error) {
        console.error('Erro ao carregar guia:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Atualizar relógio a cada minuto
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Sincronizar scroll horizontal
  const handleScroll = (e) => {
    if (timeHeaderRef.current) {
      timeHeaderRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const handleChannelClick = (channel) => {
    setSelectedChannel(channel);

    // Atualizar programa selecionado para o atual deste canal
    const now = new Date();
    const currentProg = programs[channel.id]?.find(p =>
      p.start <= now && p.stop > now
    );
    setSelectedProgram(currentProg || null);
  };

  const handleProgramClick = (program, channel) => {
    setSelectedProgram(program);
    setSelectedChannel(channel);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // Calcular posição da linha do tempo atual
  // Baseado na hora de início da grade (GRID_START_HOUR)
  const getCurrentTimePosition = () => {
    const now = currentTime;
    const startOfDay = new Date(now);
    startOfDay.setHours(GRID_START_HOUR, 0, 0, 0);

    const diffMinutes = (now - startOfDay) / 1000 / 60;
    return Math.max(0, diffMinutes * PIXELS_PER_MINUTE);
  };

  // Gerar cabeçalho de horários (24 horas a partir do início)
  const renderTimeHeader = () => {
    const slots = [];
    for (let i = 0; i < 24; i++) {
      const hour = (GRID_START_HOUR + i) % 24;
      const timeLabel = `${hour.toString().padStart(2, '0')}:00`;
      slots.push(
        <TimeSlot key={i} style={{ minWidth: `${HOUR_WIDTH}px` }}>
          {timeLabel}
        </TimeSlot>
      );
    }
    return slots;
  };

  if (loading) {
    return (
      <LoadingOverlay>
        <div className="spinner"></div>
        <div>{loadingProgress.message}</div>
        <ProgressBarContainer>
          <ProgressBarFill $percent={loadingProgress.percent} />
        </ProgressBarContainer>
        <LoadingText>{loadingProgress.percent}%</LoadingText>
      </LoadingOverlay>
    );
  }

  return (
    <MainLayout>
      <GuideContainer>
        <TopSection>
          <PlayerWrapper>
            {selectedChannel && (
              <Player
                channelId={selectedChannel.apiId || selectedChannel.id}
                isPlaying={true}
                volume={1}
              />
            )}
          </PlayerWrapper>

          <ProgramDetails>
            {selectedChannel && (
              <ChannelBadge>
                <LazyImage src={selectedChannel.logo} alt={selectedChannel.name} />
                <span>{selectedChannel.name}</span>
              </ChannelBadge>
            )}

            {selectedProgram ? (
              <>
                <ProgramTitle>{selectedProgram.title}</ProgramTitle>
                <ProgramTime>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {formatTime(selectedProgram.start)} - {formatTime(selectedProgram.stop)}
                </ProgramTime>
                <ProgramDesc>
                  {selectedProgram.description || 'Sem descrição disponível.'}
                </ProgramDesc>
              </>
            ) : (
              <ProgramTitle>Selecione um programa</ProgramTitle>
            )}
          </ProgramDetails>
        </TopSection>

        <GridSection>
          <TimeHeader ref={timeHeaderRef}>
            {renderTimeHeader()}
            <CurrentTimeLine $left={getCurrentTimePosition() + 200} /> {/* +200 offset da coluna de canais */}
          </TimeHeader>

          <GridBody ref={gridRef} onScroll={handleScroll}>
            <CurrentTimeLine
              $left={getCurrentTimePosition() + 200}
              style={{ height: channels.length * 80 }}
            />

            {channels.map(channel => {
              const channelPrograms = programs[channel.id] || [];

              // Filtrar programas para mostrar apenas os das próximas 24h a partir do inicio da grade
              const gridStart = new Date(currentTime);
              gridStart.setHours(GRID_START_HOUR, 0, 0, 0);

              return (
                <ChannelRow key={channel.id}>
                  <ChannelHeader onClick={() => handleChannelClick(channel)}>
                    <LazyImage src={channel.logo} alt={channel.name} />
                    <span>{channel.name}</span>
                  </ChannelHeader>

                  <ProgramsTrack style={{ width: `${HOUR_WIDTH * 24}px` }}>
                    {channelPrograms.map((program, index) => {
                      // Calcular posição e largura
                      const startDiff = (program.start - gridStart) / 1000 / 60;
                      const duration = (program.stop - program.start) / 1000 / 60;

                      // Se o programa terminou antes do início da grade ou começa depois do fim, não renderizar
                      if (startDiff + duration < 0 || startDiff > 24 * 60) return null;

                      const left = Math.max(0, startDiff * PIXELS_PER_MINUTE);
                      const width = Math.max(10, duration * PIXELS_PER_MINUTE); // Mínimo 10px

                      const isActive = selectedProgram === program;
                      const isLive = program.start <= currentTime && program.stop > currentTime;

                      return (
                        <ProgramBlock
                          key={index}
                          $left={left}
                          $width={width}
                          $active={isActive || isLive}
                          onClick={() => handleProgramClick(program, channel)}
                          title={`${program.title} (${formatTime(program.start)} - ${formatTime(program.stop)})`}
                        >
                          <h4>{program.title}</h4>
                          <span>{formatTime(program.start)} - {formatTime(program.stop)}</span>
                        </ProgramBlock>
                      );
                    })}
                  </ProgramsTrack>
                </ChannelRow>
              );
            })}
          </GridBody>
        </GridSection>
      </GuideContainer>
    </MainLayout>
  );
};

export default TVGuide;
